import { Camera, ChevronDown, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { LandingImage } from "../content";
import { PHOTO_BRIEFS } from "../photoBriefs";
import { useShowPhotoBrief } from "../useShowPhotoBrief";
import { PlaceholderBadge } from "./PlaceholderBadge";

type Tone = "light" | "dark";

// A single image slot on the landing. It renders one of two things:
//   - a REAL image (`image` present and not a placeholder) as a full-bleed <img>;
//   - otherwise a designed, on-brand PLACEHOLDER tile (warm gradient + icon + a
//     "miejsce na zdjęcie" label) — never an ugly broken image or a bare box.
// Either way, when the author-brief gate is on (dev or `?brief`), a collapsible
// "Sugestia dla fotografa" is overlaid so the shot intent sits exactly where the
// photo will land. Swapping in a real photo is a one-field edit in content.ts
// (point `src`, flip `placeholder:false`); no component change.
export function PhotoSlot({
  image,
  alt,
  briefId,
  aspectClassName = "aspect-[4/3]",
  tone = "light",
  icon: Icon = Camera,
  label,
  className,
  imgClassName,
  loading = "lazy",
  priority = false,
}: {
  image?: LandingImage;
  /** Already-translated alt text (used only when a real image renders). */
  alt?: string;
  briefId?: keyof typeof PHOTO_BRIEFS;
  aspectClassName?: string;
  tone?: Tone;
  icon?: LucideIcon;
  label?: string;
  className?: string;
  imgClassName?: string;
  loading?: "lazy" | "eager";
  priority?: boolean;
}) {
  const showBrief = useShowPhotoBrief();
  const brief = briefId ? PHOTO_BRIEFS[briefId] : undefined;
  const hasReal = image != null && !image.placeholder;

  return (
    <div
      className={cn(
        "relative isolate overflow-hidden rounded-2xl",
        tone === "dark" ? "ring-1 ring-white/10" : "ring-1 ring-black/5",
        aspectClassName,
        className,
      )}
    >
      {hasReal && image ? (
        <img
          src={image.src}
          alt={alt ?? ""}
          width={image.width}
          height={image.height}
          loading={loading}
          fetchPriority={priority ? "high" : "auto"}
          className={cn("absolute inset-0 h-full w-full object-cover", imgClassName)}
        />
      ) : (
        <PlaceholderTile tone={tone} Icon={Icon} label={label} />
      )}

      {image?.placeholder ? <PlaceholderBadge /> : null}

      {showBrief && brief ? <PhotoBriefDetails brief={brief} /> : null}
    </div>
  );
}

// The warm, tasteful stand-in shown until a real photo lands. Not a "colored div
// where a photo belongs": it's a labelled, on-brand pre-shoot placeholder whose
// whole job is to read as complete while making the empty slot obvious to swap.
function PlaceholderTile({ tone, Icon, label }: { tone: Tone; Icon: LucideIcon; label?: string }) {
  const dark = tone === "dark";
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 flex flex-col items-center justify-center gap-3"
      style={{
        background: dark
          ? "radial-gradient(120% 120% at 30% 20%, #33281f 0%, #241b14 60%, #1c140e 100%)"
          : "radial-gradient(120% 120% at 30% 20%, #f4ebd7 0%, #ece0c8 55%, #e4d7bd 100%)",
      }}
    >
      <span
        className={cn(
          "flex h-14 w-14 items-center justify-center rounded-full",
          dark ? "bg-white/10" : "bg-black/5",
        )}
        style={{ color: dark ? "rgba(255,240,214,0.72)" : "#a1917a" }}
      >
        <Icon className="h-6 w-6" strokeWidth={1.5} />
      </span>
      <span
        className="px-4 text-center text-xs font-medium tracking-wide"
        style={{ color: dark ? "rgba(255,247,232,0.6)" : "#6f6350" }}
      >
        {label ?? "Miejsce na zdjęcie"}
      </span>
    </div>
  );
}

// The author-only collapsible. Native <details>/<summary>: works without JS,
// keyboard-accessible, no library. Gated to dev/`?brief` by the parent, so it
// never reaches a customer or the prerendered HTML.
// Standalone, gated brief for slots that aren't a PhotoSlot tile (e.g. under the
// before/after slider or a testimonial). Renders as a static block, not overlaid.
export function PhotoBrief({
  briefId,
  className,
}: {
  briefId: keyof typeof PHOTO_BRIEFS;
  className?: string;
}) {
  const showBrief = useShowPhotoBrief();
  const brief = PHOTO_BRIEFS[briefId];
  if (!showBrief || !brief) return null;
  return (
    <div className={cn("mx-auto max-w-md", className)}>
      <PhotoBriefDetails brief={brief} floating={false} />
    </div>
  );
}

function PhotoBriefDetails({
  brief,
  floating = true,
}: {
  brief: (typeof PHOTO_BRIEFS)[string];
  floating?: boolean;
}) {
  return (
    <details
      className={cn(
        "group z-20 [&_summary::-webkit-details-marker]:hidden",
        floating ? "absolute inset-x-2 bottom-2" : "relative",
      )}
      data-testid="photo-brief"
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 rounded-full bg-foreground/85 px-3 py-1.5 text-xs font-medium text-white shadow-md backdrop-blur-sm transition hover:bg-foreground">
        <Camera className="h-3.5 w-3.5" aria-hidden="true" />
        <span>Sugestia dla fotografa</span>
        <ChevronDown
          className="ml-auto h-3.5 w-3.5 transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>
      <div className="mt-2 max-h-56 overflow-y-auto rounded-xl bg-foreground/92 p-3 text-left text-white shadow-lg backdrop-blur-sm">
        <div className="mb-1.5 flex items-center gap-2">
          <span className="rounded bg-white/15 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide">
            {brief.source}
          </span>
          <span className="text-[13px] font-semibold">{brief.title}</span>
        </div>
        <p className="text-[12px] leading-relaxed text-white/85">{brief.note}</p>
      </div>
    </details>
  );
}
